'use client';

import { useCallback, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Club, ClubClosure } from '@court-sync/shared';
import { getDefaultClubOperatingHours } from '@court-sync/shared';
import { resolveEffectiveClubOperatingHours } from '@court-sync/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Field,
  FieldLabel,
  Input,
} from '@/components/ui';
import {
  OperatingHoursScheduleEditor,
  dayScheduleRowsToSlots,
  toDayScheduleRows,
  type DayScheduleRow,
} from '@/components/club/OperatingHoursScheduleEditor';
import { clubHoursService } from '@/services/clubHours';

function formatClosureRange(closure: ClubClosure): string {
  const start = format(new Date(closure.startAt), 'MMM d, yyyy HH:mm');
  const end = format(new Date(closure.endAt), 'MMM d, yyyy HH:mm');
  return `${start} – ${end}`;
}

interface ClubHoursEditorProps {
  club: Club;
  /** When false (academy-only), copy refers to court use or hire instead of court hire alone. */
  hasCourtHire?: boolean;
}

export function ClubHoursEditor({ club, hasCourtHire = true }: ClubHoursEditorProps) {
  const courtUseLabel = hasCourtHire ? 'court hire' : 'court use or hire';
  const [rows, setRows] = useState<DayScheduleRow[]>(() => toDayScheduleRows(getDefaultClubOperatingHours()));
  const [closures, setClosures] = useState<ClubClosure[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingHours, setSavingHours] = useState(false);
  const [savingClosure, setSavingClosure] = useState(false);
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [closureStart, setClosureStart] = useState('');
  const [closureEnd, setClosureEnd] = useState('');
  const [closureReason, setClosureReason] = useState('');
  const [usesCustomHours, setUsesCustomHours] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [hoursRes, closuresRes] = await Promise.all([
        clubHoursService.getOperatingHours(club.id),
        clubHoursService.getClosures(club.id),
      ]);
      if (hoursRes.success && hoursRes.data) {
        const effective = resolveEffectiveClubOperatingHours(hoursRes.data);
        setUsesCustomHours(hoursRes.data.length > 0);
        setRows(toDayScheduleRows(effective));
      }
      if (closuresRes.success && closuresRes.data) {
        setClosures(closuresRes.data);
      }
    } catch {
      toast.error('Failed to load operating hours');
    } finally {
      setLoading(false);
    }
  }, [club.id]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSaveHours = async () => {
    setSavingHours(true);
    try {
      const slots = dayScheduleRowsToSlots(rows);
      if (slots.length === 0) {
        toast.error('At least one day must be open');
        return;
      }
      const res = await clubHoursService.updateOperatingHours(club.id, slots);
      if (res.success) {
        setUsesCustomHours(true);
        toast.success('Operating hours saved');
      } else {
        toast.error(res.error || 'Failed to save operating hours');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save operating hours');
    } finally {
      setSavingHours(false);
    }
  };

  const handleAddClosure = async () => {
    if (!closureStart || !closureEnd) {
      toast.error('Start and end times are required');
      return;
    }
    const start = new Date(closureStart);
    const end = new Date(closureEnd);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      toast.error('Choose a valid closure window');
      return;
    }
    setSavingClosure(true);
    try {
      const res = await clubHoursService.addClosure(club.id, {
        startAt: start.toISOString(),
        endAt: end.toISOString(),
        reason: closureReason.trim() || undefined,
      });
      if (res.success) {
        toast.success('Closure added');
        setClosureStart('');
        setClosureEnd('');
        setClosureReason('');
        setClosureDialogOpen(false);
        await loadData();
      } else {
        toast.error(res.error || 'Failed to add closure');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add closure');
    } finally {
      setSavingClosure(false);
    }
  };

  const handleRemoveClosure = async (closureId: string) => {
    try {
      const res = await clubHoursService.removeClosure(club.id, closureId);
      if (res.success) {
        toast.success('Closure removed');
        setClosures((prev) => prev.filter((c) => c.id !== closureId));
      } else {
        toast.error(res.error || 'Failed to remove closure');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove closure');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center" aria-busy="true">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <Card>
        <CardHeader>
          <CardTitle>Default opening hours</CardTitle>
          <CardDescription>
            Set when courts are available for hire. Times use your club timezone ({club.timezone}).
            {!usesCustomHours ? ' Until you save custom hours, court hire uses 6:00 AM – 10:00 PM daily.' : null}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <OperatingHoursScheduleEditor rows={rows} onChange={setRows} idPrefix="club-hours" />
          <Button type="button" onClick={() => void handleSaveHours()} isLoading={savingHours}>
            Save operating hours
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle>Closure periods</CardTitle>
            <CardDescription>
              Block {courtUseLabel} on all courts during maintenance, events, or holidays.
            </CardDescription>
          </div>
          <Dialog open={closureDialogOpen} onOpenChange={setClosureDialogOpen}>
            <DialogTrigger asChild>
              <Button type="button" variant="outline" size="sm">
                <Plus className="mr-2 h-4 w-4" aria-hidden />
                Add closure
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add closure</DialogTitle>
                <DialogDescription>
                  Courts cannot be hired during this period. Use your local club time when picking dates.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <Field>
                  <FieldLabel htmlFor="closure-start">Starts</FieldLabel>
                  <Input
                    id="closure-start"
                    type="datetime-local"
                    value={closureStart}
                    onChange={(e) => setClosureStart(e.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="closure-end">Ends</FieldLabel>
                  <Input
                    id="closure-end"
                    type="datetime-local"
                    value={closureEnd}
                    onChange={(e) => setClosureEnd(e.target.value)}
                  />
                </Field>
                <Field>
                  <FieldLabel htmlFor="closure-reason">Reason (optional)</FieldLabel>
                  <Input
                    id="closure-reason"
                    value={closureReason}
                    onChange={(e) => setClosureReason(e.target.value)}
                    placeholder="e.g. Court resurfacing"
                  />
                </Field>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setClosureDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" onClick={() => void handleAddClosure()} isLoading={savingClosure}>
                  Add closure
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {closures.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No upcoming or past closures configured.</p>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {closures.map((closure) => (
                <li key={closure.id} className="flex items-start justify-between gap-3 py-3 first:pt-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatClosureRange(closure)}
                    </p>
                    {closure.reason ? (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{closure.reason}</p>
                    ) : null}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleRemoveClosure(closure.id)}
                    aria-label="Remove closure"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
